<template>
  <div class="textarea" :class="{ 'textarea--error': errors.length }">
    <label v-if="label" class="textarea__label" :for="name">
      {{ label }}
    </label>
    <div class="textarea__wrapper">
      <span v-if="prefix" class="textarea__prefix">{{ prefix }}</span>
      <UIIcon v-if="leftIcon" class="textarea__left-icon" :name="leftIcon" filled />
      <textarea
        :id="id"
        ref="textarea"
        v-mask
        :data-maska="dataMaska"
        :value="inputValue"
        :name="name"
        :autocomplete="autocomplete"
        :disabled="disabled"
        class="textarea__field"
        :class="[
          { 'textarea__field--error': errors.length },
          { 'textarea__field--with-left-icon': leftIcon },
          { 'textarea__field--with-right-icon': rightIcon },
          { 'textarea__field--with-prefix': prefix },
          { 'textarea__field--with-suffix': suffix },
          { 'textarea__field--compact': compact },
          inputClass,
        ]"
        :placeholder="placeholder"
        :style="{ minHeight: `${minHeight}px` }"
        :maxlength="maxLength"
        @focus="handleFocus"
        @input="handleChange"
        @blur="handleBlurEvent"
      />
      <UIIcon v-if="rightIcon" class="textarea__right-icon" :name="rightIcon" filled />
      <UIBaseTransition>
        <UIIcon v-if="clearable && inputValue" class="textarea__clear-icon" name="close" filled @click="handleClear" />
      </UIBaseTransition>
      <span v-if="suffix" class="textarea__suffix">{{ suffix }}</span>
      <span v-if="errorMessage?.trim()" class="text-left text-error-100">{{ $t(errorMessage) }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useField } from 'vee-validate'
import type { InputTextProps } from '../../types/ui'

const props = withDefaults(
  defineProps<Omit<InputTextProps, 'modelValue'> & { modelValue?: string; minHeight?: string }>(),
  {
    autocomplete: undefined,
    placeholder: 'Placeholder',
    modelValue: undefined,
    value: undefined,
    id: undefined,
    label: '',
    maxLength: '',
    disabled: false,
    leftIcon: '',
    rightIcon: '',
    prefix: '',
    suffix: '',
    type: 'text',
    clearable: false,
    syncVModel: false,
    description: '',
    dataMaska: '',
    controlled: true,
    minHeight: '154',
    inputClass: '',
  }
)

const emit = defineEmits<{
  (e: 'blur', event: FocusEvent): void
  (e: 'focus', event: FocusEvent): void
  (e: 'update:modelValue', event: string): void
  (e: 'clear', value: ''): void
}>()

const name = toRef(props, 'name')

const {
  value: inputValue,
  errorMessage,
  handleBlur,
  handleChange,
  resetField,
  errors,
} = useField(name, undefined, {
  initialValue: props.modelValue || props.value,
  controlled: props.controlled,
  syncVModel: props.syncVModel,
})

const handleClear = () => {
  resetField({
    value: '',
  })
  emit('clear', '')
}

const isFocused = ref(false)

const handleBlurEvent = (event: FocusEvent) => {
  handleBlur(event)
  emit('blur', event)

  isFocused.value = false
}

const handleFocus = (event: FocusEvent) => {
  isFocused.value = true
  emit('focus', event)
}

const textarea = ref<HTMLTextAreaElement | undefined>(undefined)

useTextareaAutosize({
  element: textarea,
  input: inputValue,
})
</script>

<style scoped lang="scss">
@use '@/styles/variables.scss' as *;

$basePadding: 16px;
$iconSize: 24px;

.textarea {
  color: $black-80;
  font-size: 14px;
  line-height: 140%;

  &--error {
    .textarea__label {
      color: $error-100;
    }
  }

  &__label {
    display: block;
    margin-bottom: 2px;
    margin-left: 16px;
    font-weight: 500;
  }

  &__wrapper {
    display: flex;
    flex-direction: column;
    width: 100%;
    position: relative;
  }

  &__left-icon,
  &__right-icon,
  &__clear-icon {
    width: $iconSize;
    height: $iconSize;
    color: $black-60;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1;
  }

  &__left-icon,
  &__prefix {
    position: absolute;
    left: $basePadding;
    top: 50%;
    transform: translateY(-50%);
  }

  &__right-icon,
  &__suffix,
  &__clear-icon {
    position: absolute;
    right: $basePadding;
    top: 50%;
    transform: translateY(-50%);
  }

  &__clear-icon {
    cursor: pointer;
  }

  &__field {
    box-shadow: none;
    padding: 12px $basePadding;
    border-radius: $base-border-radius;
    font-size: 14px;
    color: $black-100;
    min-height: 48px;
    transition: $base-transition border;
    width: 100%;
    border: $base-border-width solid $black-20;
    resize: none;
    outline: none;
    overflow-y: hidden;
    line-height: normal;

    &--error {
      border: $base-border-width solid $error-100;
    }

    &--with-left-icon {
      padding-left: calc($iconSize + $basePadding + 4px) !important;
    }

    &--with-right-icon {
      padding-right: calc($iconSize + $basePadding + 4px) !important;
    }

    &--compact {
      padding: 10px 16px;
    }

    &:disabled {
      background: $black-05;
      color: $black-60;
      border: $base-border-width solid $black-20 !important;
    }

    &:focus {
      box-shadow: none;
      border: $base-border-width solid $black-100 !important;
    }

    &:hover {
      border: $base-border-width solid $black-60;
    }

    &::placeholder {
      color: $black-40;
    }
  }

  &__description,
  &__error {
    color: $black-60;
    margin-top: 2px;
    display: block;
    margin-left: 16px;
    font-size: 12px;
  }
}
</style>
